import React, { useState, useEffect } from 'react';
import { Card, Button, Badge } from 'react-bootstrap';
import { callCenterApi } from '@/_services/api/callCenter';
import { toast } from 'react-toastify';
import { BsArrowRepeat as RefreshIcon, BsGraphUp } from 'react-icons/bs';
import CampaignMetricsModal from './CampaignMetricsModal';
import { ProgressBar } from 'react-bootstrap';
import styled from '@emotion/styled';
import ComponentErrorBoundary from '@/Layouts/ComponentErrorBoundary';
import { VscLoading } from 'react-icons/vsc';
import Swal from 'sweetalert2';
const AUTO_REFRESH_INTERVAL = 15000;

const StyledCard = styled.div`
    .call-progress {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 4px;
        background: #e9ecef;
        overflow: hidden;

        .progress-bar {
            background: linear-gradient(90deg, #28a745, #20c997);
            animation: progress-animation 2s ease-in-out infinite;

            @keyframes progress-animation {
                0% {
                    transform: translateX(-100%);
                }
                100% {
                    transform: translateX(100%);
                }
            }
        }
    }

        .call-card {
        border: none;
        border-radius: 12px;
        overflow: hidden;
        transition: all 0.3s ease;
        box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);

        &:hover {
            transform: translateY(-4px);
            box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
        }
`;
const Component = ({ teamId, teams }) => {
    const [queues, setQueues] = useState([]);
    const [loadingQueues, setLoadingQueues] = useState(false);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [showMetricsModal, setShowMetricsModal] = useState(false);

    const handleViewMetrics = (campaign) => {
        setSelectedCampaign(campaign);
        setShowMetricsModal(true);
    };

    const loadQueues = async () => {
        try {
            if (loadingQueues) return;
            setLoadingQueues(true);
            const res = await callCenterApi.getQueues({ team: teamId, teams });
            setQueues(res?.results || []);
        } catch (error) {
            console.error('Error loading queues:', error);
            // toast.error('Failed to load queues');
        } finally {
            setLoadingQueues(false);
        }
    };

    useEffect(() => {
        if (teamId || teams) {
            loadQueues();
            const interval = setInterval(loadQueues, AUTO_REFRESH_INTERVAL);
            return () => clearInterval(interval);
        }
    }, [teamId, teams]);

    return (
        <Card className="h-100">
            <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                        Campaign Queue: <span>{(queues || []).length}</span>
                    </h5>
                    <Button variant="outline-primary" onClick={loadQueues}>
                        <RefreshIcon className={`${loadingQueues ? 'spin' : ''}`} />
                    </Button>
                </div>
            </Card.Header>
            <Card.Body style={{ maxHeight: '600px', overflowY: 'auto' }}>
                {loadingQueues && queues.length === 0 ? (
                    <div className="text-center py-4">
                        <VscLoading className="spin" size={24} />
                    </div>
                ) : queues.length === 0 ? (
                    <div className="text-center text-muted py-5">
                        <div className="mb-2" style={{ fontSize: '2rem' }}>
                            👥
                        </div>
                        <p className="mb-0">No active dialing queues</p>
                    </div>
                ) : (
                    queues.map((queue) => {
                        const isActive = queue.status === 'active';
                        const isPaused = queue.status === 'paused';
                        const campaignId = queue.campaign?._id;
                        const campaignName = queue.campaign?.name || '';

                        const handleQueueControl = async (action) => {
                            try {
                                const result = await Swal.fire({
                                    title: `Are you sure you want to ${action} the campaign: ${campaignName}?`,
                                    icon: 'warning',
                                    showCancelButton: true,
                                    confirmButtonColor: '#3085d6',
                                    cancelButtonColor: '#d33',
                                    confirmButtonText: `Yes, ${action}`,
                                    cancelButtonText: 'Cancel',
                                });
                                if (!result?.isConfirmed) {
                                    return;
                                }

                                if (action === 'start') {
                                    await callCenterApi.startCampaign(campaignId);
                                } else if (action === 'pause') {
                                    await callCenterApi.pauseCampaign(campaignId);
                                } else if (action === 'resume') {
                                    await callCenterApi.resumeCampaign(campaignId);
                                } else if (action === 'stop') {
                                    await callCenterApi.stopCampaign(campaignId);
                                }
                                toast.success(`Queue ${action} successful`);
                                // Refresh queues so Live Ops reflects the latest status
                                loadQueues();
                            } catch (err) {
                                console.error(`Error performing queue action '${action}'`, err);
                                toast.error(
                                    `Failed to ${action} queue: ${
                                        err?.message || err?.response?.data?.message || 'Unknown error'
                                    }`
                                );
                            }
                        };

                        return (
                            <StyledCard>
                                <Card key={queue._id} className="mb-3 call-card ">
                                    <Card.Body>
                                        <div className="d-flex justify-content-between align-items-start mb-1">
                                            <div>
                                                <div className="fw-semibold">{campaignName}</div>
                                                <div className="text-muted small">ID: {campaignId}</div>
                                            </div>
                                            <Badge bg={isActive ? 'success' : isPaused ? 'warning' : 'secondary'}>
                                                {queue.status || 'inactive'}
                                            </Badge>
                                        </div>
                                        <div className="d-flex justify-content-between mt-2 small text-muted">
                                            <span>Queued</span>
                                            <span>
                                                {queue.metrics?.totalContacts ??
                                                    0 - queue.metrics?.processedContacts ??
                                                    0}
                                            </span>
                                        </div>
                                        <div className="d-flex justify-content-between small text-muted mb-2">
                                            <span>Processed</span>
                                            <span>{queue.metrics?.processedContacts ?? 0}</span>
                                        </div>
                                        <div className="d-flex justify-content-between small text-muted mb-2">
                                            <span>Total</span>
                                            <span>{queue.metrics?.totalContacts ?? 0}</span>
                                        </div>

                                        <div className="d-flex gap-2 mt-2">
                                            {!isActive && !isPaused && (
                                                <Button
                                                    variant="success"
                                                    size="sm"
                                                    onClick={() => handleQueueControl('start')}
                                                >
                                                    ▶️ Start
                                                </Button>
                                            )}
                                            {isActive && (
                                                <Button
                                                    variant="warning"
                                                    size="sm"
                                                    onClick={() => handleQueueControl('pause')}
                                                >
                                                    ⏸️ Pause
                                                </Button>
                                            )}
                                            {isPaused && (
                                                <Button
                                                    variant="success"
                                                    size="sm"
                                                    onClick={() => handleQueueControl('resume')}
                                                >
                                                    ▶️ Resume
                                                </Button>
                                            )}
                                            {(isActive || isPaused) && (
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => handleQueueControl('stop')}
                                                >
                                                    ⏹️ Stop
                                                </Button>
                                            )}

                                            {(isActive || isPaused) && (
                                                <Button
                                                    variant="outline-info"
                                                    size="sm"
                                                    onClick={() =>
                                                        handleViewMetrics({ _id: campaignId, name: campaignName })
                                                    }
                                                >
                                                    <BsGraphUp className="me-1" /> Metrics
                                                </Button>
                                            )}
                                        </div>
                                    </Card.Body>
                                    {(isActive || isPaused) && (
                                        <ProgressBar
                                            now={100}
                                            animated
                                            className="call-progress "
                                            style={isPaused ? { background: 'orange' } : {}}
                                        />
                                    )}
                                </Card>
                            </StyledCard>
                        );
                    })
                )}
            </Card.Body>
            <CampaignMetricsModal
                show={showMetricsModal}
                onHide={() => setShowMetricsModal(false)}
                campaignId={selectedCampaign?._id}
                campaignName={selectedCampaign?.name}
            />
        </Card>
    );
};

const CallCenterCampaignQueue = (props) => (
    <ComponentErrorBoundary componentErrorMessage={props?.componentErrorMessage}>
        <Component {...props} />
    </ComponentErrorBoundary>
);

export default CallCenterCampaignQueue;
